import React, { useRef, useState, useEffect, useCallback } from "react";
 
interface ResizableColumnHeaderProps {
  children: React.ReactNode;
  onResize: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  className?: string;
  style?: React.CSSProperties;
}
 
const ResizableColumnHeader: React.FC<ResizableColumnHeaderProps> = ({
  children,
  onResize,
  minWidth = 50,
  maxWidth = 1000,
  className = "",
  style = {},
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
 
  // Get actual width from parent th element
  const getActualWidth = useCallback(() => {
    if (headerRef.current) {
      const th = headerRef.current.closest('th');
      if (th) {
        return th.offsetWidth;
      }
    }
    return 0;
  }, []);
 
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const currentWidth = getActualWidth();
      if (currentWidth > 0) {
        setIsResizing(true);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
       
        // Prevent text selection during resize
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
      }
    },
    [getActualWidth]
  );
 
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
 
      const diff = e.clientX - startX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + diff));
     
      // Update the actual th element width
      if (headerRef.current) {
        const th = headerRef.current.closest('th');
        if (th) {
          th.style.width = `${newWidth}px`;
          th.style.minWidth = `${newWidth}px`;
        }
      }
     
      onResize(newWidth);
    },
    [isResizing, startX, startWidth, minWidth, maxWidth, onResize]
  );
 
  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
  }, [isResizing]);
 
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
     
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);
 
  return (
    <div
      ref={headerRef}
      className={`resizable-column-header ${className}`}
      style={{
        ...style,
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      <div
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingRight: "12px", // Add padding to prevent text from overlapping resizer
          width: "100%",
          height: "100%",
        }}
      >
        {children}
      </div>
      <div
        ref={resizerRef}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: "absolute",
          top: 0,
          right: "-4px", // Position at the border edge (extends into border area for easier targeting)
          width: "8px", // Wider for easier targeting
          height: "100%",
          cursor: "col-resize",
          backgroundColor: isResizing
            ? "rgba(24, 144, 255, 0.3)"
            : isHovered
            ? "rgba(24, 144, 255, 0.1)"
            : "transparent",
          zIndex: 10,
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: isResizing ? "none" : "background-color 0.15s ease",
        }}
      >
        {/* Visual indicator line - always visible but more prominent on hover/resize */}
        <div
          style={{
            width: "2px",
            height: isHovered || isResizing ? "70%" : "60%",
            backgroundColor: isResizing ? "#1890ff" : isHovered ? "#1890ff" : "#bfbfbf",
            borderRadius: "1px",
            opacity: isResizing ? 1 : isHovered ? 1 : 0.6,
            transition: isResizing ? "none" : "all 0.15s ease",
            boxShadow: isResizing ? "0 0 4px rgba(24, 144, 255, 0.5)" : "none",
          }}
        />
      </div>
    </div>
  );
};
 
export default ResizableColumnHeader;